import { useCallback, useEffect, useState } from "react";

export function useStickyTableHeaderHeight<T extends HTMLElement = HTMLTableSectionElement>(fallbackHeight = 40) {
    const [node, setNode] = useState<T | null>(null);
    const [headerHeight, setHeaderHeight] = useState(fallbackHeight);

    const headerRef = useCallback((element: T | null) => {
        setNode(element);
    }, []);

    useEffect(() => {
        if (!node) {
            return;
        }

        const measureHeaderHeight = () => {
            const rowElements = Array.from(node.querySelectorAll("tr"));
            const rowsTotalHeight = rowElements.reduce((total, row) => total + row.getBoundingClientRect().height, 0);
            const sectionHeight = node.getBoundingClientRect().height;
            const measuredHeight = Math.ceil(rowsTotalHeight || sectionHeight);
            return measuredHeight || fallbackHeight;
        };

        const updateHeight = () => setHeaderHeight(measureHeaderHeight());
        updateHeight();

        const observer = new ResizeObserver(updateHeight);
        observer.observe(node);
        Array.from(node.querySelectorAll("tr")).forEach((row) => observer.observe(row));

        const mutationObserver = new MutationObserver(() => {
            Array.from(node.querySelectorAll("tr")).forEach((row) => observer.observe(row));
            updateHeight();
        });
        mutationObserver.observe(node, { childList: true, subtree: true });

        return () => {
            mutationObserver.disconnect();
            observer.disconnect();
        };
    }, [fallbackHeight, node]);

    return {
        headerRef,
        headerHeight,
    };
}
